class EngravingCustomizer extends HTMLElement {
  connectedCallback() {
    this.blade = {
      textInput: this.querySelector('[data-input="blade-text"]'),
      fontInput: this.querySelector('[data-input="blade-font"]'),
      sizeInput: this.querySelector('[data-input="blade-size"]'),
      rotationInput: this.querySelector('[data-input="blade-rotation"]'),
      preview: this.querySelector('[data-preview="blade"]'),
      previewText: this.querySelector('[data-preview-text="blade"]'),
      resetButton: this.querySelector('[data-action="reset-blade"]'),
      counter: this.querySelector('[data-counter="blade"]'),
      warning: this.querySelector('[data-warning="blade"]'),
      x: this.#number(this.dataset.bladeDefaultX, 38),
      y: this.#number(this.dataset.bladeDefaultY, 43),
    };

    this.sheath = {
      textInput: this.querySelector('[data-input="sheath-text"]'),
      fontInput: this.querySelector('[data-input="sheath-font"]'),
      previewText: this.querySelector('[data-preview-text="sheath"]'),
      counter: this.querySelector('[data-counter="sheath"]'),
      warning: this.querySelector('[data-warning="sheath"]'),
      x: this.#number(this.dataset.sheathDefaultX, 38),
      y: this.#number(this.dataset.sheathDefaultY, 54),
    };

    this.properties = new Map(
      Array.from(this.querySelectorAll('[data-property]')).map((input) => [/** @type {HTMLElement} */ (input).dataset.property, input])
    );
    this.snapshotInputs = {
      blade: this.querySelector('[data-snapshot-file="blade"]'),
    };
    this.productForm = this.snapshotInputs.blade?.form || this.properties.get('blade-text')?.form;

    this.#bindEvents();
    this.#render();
  }

  disconnectedCallback() {
    this.productForm?.removeEventListener('submit', this.#handleSubmit, { capture: true });
  }

  #bindEvents() {
    this.blade.textInput?.addEventListener('input', () => this.#render({ animateBlade: true }));
    this.blade.textInput?.addEventListener('change', () => this.#render());
    this.sheath.textInput?.addEventListener('input', () => this.#render({ animateSheath: true }));
    this.sheath.textInput?.addEventListener('change', () => this.#render());

    ['input', 'change'].forEach((eventName) => {
      this.blade.fontInput?.addEventListener(eventName, () => this.#render());
      this.blade.sizeInput?.addEventListener(eventName, () => this.#render());
      this.blade.rotationInput?.addEventListener(eventName, () => this.#render());
      this.sheath.fontInput?.addEventListener(eventName, () => this.#render());
    });

    this.blade.resetButton?.addEventListener('pointerdown', this.#handleBladeReset);
    this.blade.resetButton?.addEventListener('click', this.#handleBladeReset);

    this.blade.previewText?.addEventListener('pointerdown', (event) => this.#startDrag(event));
    this.blade.previewText?.addEventListener('keydown', (event) => this.#nudgeBlade(event));
    this.productForm?.addEventListener('submit', this.#handleSubmit, { capture: true });
  }

  #handleSubmit = () => {
    this.#render();
  };

  #handleBladeReset = (event) => {
    event.preventDefault();
    event.stopPropagation();
    this.#resetBladePosition();
  };

  #resetBladePosition() {
    this.blade.x = this.#number(this.dataset.bladeDefaultX, 38);
    this.blade.y = this.#number(this.dataset.bladeDefaultY, 43);

    if (this.blade.sizeInput) {
      this.blade.sizeInput.value = String(this.#number(this.dataset.bladeDefaultSize, 32));
    }

    if (this.blade.rotationInput) {
      this.blade.rotationInput.value = '0';
    }

    this.blade.previewText?.style.setProperty('--engraving-x', this.blade.x);
    this.blade.previewText?.style.setProperty('--engraving-y', this.blade.y);
    this.#render();
  }

  #startDrag(event) {
    if (!this.blade.preview || !this.blade.previewText) return;

    event.preventDefault();
    this.blade.previewText.setPointerCapture(event.pointerId);
    this.#moveBladeToPointer(event);

    const handleMove = (moveEvent) => this.#moveBladeToPointer(moveEvent);
    const handleUp = () => {
      this.blade.previewText?.removeEventListener('pointermove', handleMove);
      this.blade.previewText?.removeEventListener('pointerup', handleUp);
      this.blade.previewText?.removeEventListener('pointercancel', handleUp);
    };

    this.blade.previewText.addEventListener('pointermove', handleMove);
    this.blade.previewText.addEventListener('pointerup', handleUp);
    this.blade.previewText.addEventListener('pointercancel', handleUp);
  }

  #moveBladeToPointer(event) {
    const rect = this.blade.preview?.getBoundingClientRect();
    if (!rect) return;

    this.blade.x = this.#clamp(((event.clientX - rect.left) / rect.width) * 100, 3, 97);
    this.blade.y = this.#clamp(((event.clientY - rect.top) / rect.height) * 100, 3, 97);
    this.#render();
  }

  #nudgeBlade(event) {
    const directions = {
      ArrowLeft: [-1, 0],
      ArrowRight: [1, 0],
      ArrowUp: [0, -1],
      ArrowDown: [0, 1],
    };

    const direction = directions[event.key];
    if (!direction) return;

    event.preventDefault();
    const step = event.shiftKey ? 5 : 1;
    this.blade.x = this.#clamp(this.blade.x + direction[0] * step, 3, 97);
    this.blade.y = this.#clamp(this.blade.y + direction[1] * step, 3, 97);
    this.#render();
  }

  #render({ animateBlade = false, animateSheath = false } = {}) {
    this.#enforceMaxLength(this.blade.textInput);
    this.#enforceMaxLength(this.sheath.textInput);

    const bladeText = this.blade.textInput?.value.trim() || '';
    const sheathText = this.sheath.textInput?.value.trim() || '';
    const bladeFontName = this.#selectedText(this.blade.fontInput);
    const sheathFontName = this.#selectedText(this.sheath.fontInput);
    const bladeSize = this.#number(this.blade.sizeInput?.value, 32);
    const bladeRotation = this.#number(this.blade.rotationInput?.value, 0);

    this.#renderCharacterState(this.blade.textInput, this.blade.counter, this.blade.warning);
    this.#renderCharacterState(this.sheath.textInput, this.sheath.counter, this.sheath.warning);

    this.#renderText(this.blade.previewText, {
      text: bladeText,
      fallback: 'Dein Text',
      font: this.blade.fontInput?.value,
      size: bladeSize,
      rotation: bladeRotation,
      x: this.blade.x,
      y: this.blade.y,
    });
    this.#animateEngravingText(this.blade.previewText, animateBlade && Boolean(bladeText));

    this.#renderText(this.sheath.previewText, {
      text: sheathText,
      fallback: 'Dein Text',
      font: this.sheath.fontInput?.value,
      size: 21,
      rotation: 0,
      x: this.sheath.x,
      y: this.sheath.y,
    });
    this.#animateEngravingText(this.sheath.previewText, animateSheath && Boolean(sheathText));

    this.#setProperty('blade-text', bladeText, Boolean(bladeText));
    this.#setProperty('blade-font', bladeFontName, Boolean(bladeText));
    this.#setProperty('blade-x', this.blade.x.toFixed(1), Boolean(bladeText));
    this.#setProperty('blade-y', this.blade.y.toFixed(1), Boolean(bladeText));
    this.#setProperty('blade-size', String(bladeSize), Boolean(bladeText));
    this.#setProperty('blade-rotation', String(bladeRotation), Boolean(bladeText));
    this.#setBladePdfSnapshot(bladeText, bladeFontName, bladeSize, bladeRotation);

    this.#setProperty('sheath-text', sheathText, Boolean(sheathText));
    this.#setProperty('sheath-font', sheathFontName, Boolean(sheathText));
  }

  #renderText(element, state) {
    if (!element) return;

    element.textContent = state.text || state.fallback;
    element.classList.toggle('is-empty', !state.text);
    element.style.setProperty('--engraving-font', state.font);
    element.style.setProperty('--engraving-size', state.size);
    element.style.setProperty('--engraving-rotation', state.rotation);
    element.style.setProperty('--engraving-x', state.x);
    element.style.setProperty('--engraving-y', state.y);
  }

  #animateEngravingText(element, enabled) {
    if (!element || !enabled) return;

    element.classList.remove('is-engraving-animated');
    void element.offsetWidth;
    element.classList.add('is-engraving-animated');
  }

  #renderCharacterState(input, counter, warning) {
    if (!input) return;

    const maxLength = this.#maxLength(input);
    const count = input.value.length;

    if (counter) {
      counter.textContent = `${count} / ${maxLength} Zeichen`;
    }

    if (warning) {
      warning.hidden = count < maxLength;
    }
  }

  #enforceMaxLength(input) {
    if (!input) return;

    const maxLength = this.#maxLength(input);
    if (input.value.length > maxLength) {
      input.value = input.value.slice(0, maxLength);
    }
  }

  #maxLength(input) {
    const configured = this.#number(this.dataset.maxLength, 20);
    const inputMax = input.maxLength > 0 ? input.maxLength : configured;

    return Math.min(inputMax, configured, 20);
  }

  #setProperty(key, value, enabled) {
    const input = this.properties.get(key);
    if (!input) return;

    input.value = value;
    input.disabled = !enabled;
  }

  #setBladePdfSnapshot(text, fontName, size, rotation) {
    const input = this.snapshotInputs.blade;
    const preview = this.blade.preview;

    if (!input) return;

    if (!text || !preview || typeof DataTransfer === 'undefined') {
      input.value = '';
      input.disabled = true;
      return;
    }

    const rect = preview.getBoundingClientRect();
    const outputWidth = 1320;
    const outputHeight = Math.round(outputWidth * (rect.height / rect.width || 0.35));
    const scale = outputWidth / (rect.width || outputWidth);
    const x = (this.blade.x / 100) * outputWidth;
    const y = (this.blade.y / 100) * outputHeight;
    const pdf = this.#buildBladeSnapshotPdf({
      width: outputWidth,
      height: outputHeight,
      text,
      fontFamily: this.blade.fontInput?.value || 'Georgia, serif',
      fontName,
      fontSize: Math.round(size * scale * 10) / 10,
      rotation,
      x,
      y,
      positionX: this.blade.x,
      positionY: this.blade.y,
      originalSize: size,
    });

    try {
      const file = new File([pdf], 'klingen-gravur.pdf', { type: 'application/pdf' });
      const transfer = new DataTransfer();
      transfer.items.add(file);
      input.files = transfer.files;
      input.disabled = false;
    } catch (_error) {
      input.value = '';
      input.disabled = true;
    }
  }

  #buildBladeSnapshotPdf(state) {
    const canvas = document.createElement('canvas');
    canvas.width = state.width;
    canvas.height = state.height;

    const context = canvas.getContext('2d');
    if (!context) return new Uint8Array();

    this.#drawBladeSnapshot(context, state);

    const dataUrl = canvas.toDataURL('image/jpeg', 0.92);
    const imageBytes = this.#base64ToBytes(dataUrl.split(',')[1] || '');
    const pageWidth = 842;
    const pageHeight = Math.round(pageWidth * (state.height / state.width));
    const content = `q\n${pageWidth} 0 0 ${pageHeight} 0 0 cm\n/Im0 Do\nQ`;

    return this.#buildPdf({
      pageWidth,
      pageHeight,
      imageWidth: state.width,
      imageHeight: state.height,
      imageBytes,
      content,
    });
  }

  #drawBladeSnapshot(context, state) {
    context.clearRect(0, 0, state.width, state.height);
    context.fillStyle = '#f7f6f1';
    context.fillRect(0, 0, state.width, state.height);

    const yOffset = 250;
    const bladeGradient = context.createLinearGradient(88, 388 - yOffset, 1190, 544 - yOffset);
    bladeGradient.addColorStop(0, '#ecebe4');
    bladeGradient.addColorStop(0.18, '#c6c5bd');
    bladeGradient.addColorStop(0.48, '#777972');
    bladeGradient.addColorStop(0.74, '#bdbbb2');
    bladeGradient.addColorStop(1, '#e8e5dc');

    context.save();
    context.shadowColor = 'rgba(31, 31, 28, 0.22)';
    context.shadowBlur = 20;
    context.shadowOffsetY = 18;
    context.fillStyle = bladeGradient;
    context.beginPath();
    context.moveTo(113.5, 477.8 - yOffset);
    context.bezierCurveTo(170.7, 415.5 - yOffset, 260.2, 374 - yOffset, 382.4, 353.5 - yOffset);
    context.lineTo(1127.5, 353.5 - yOffset);
    context.bezierCurveTo(1160.4, 353.5 - yOffset, 1187.9, 378.7 - yOffset, 1190.8, 411.5 - yOffset);
    context.lineTo(1199.1, 506.6 - yOffset);
    context.bezierCurveTo(1202.3, 543.5 - yOffset, 1173.2, 575.2 - yOffset, 1136.2, 575.2 - yOffset);
    context.lineTo(371.5, 575.2 - yOffset);
    context.bezierCurveTo(255, 569.6 - yOffset, 169, 537.2 - yOffset, 113.5, 477.8 - yOffset);
    context.closePath();
    context.fill();
    context.restore();

    const edgeGradient = context.createLinearGradient(130, 555 - yOffset, 1128, 620 - yOffset);
    edgeGradient.addColorStop(0, '#deddd5');
    edgeGradient.addColorStop(0.45, '#faf8ee');
    edgeGradient.addColorStop(1, '#b8b6ae');
    context.globalAlpha = 0.72;
    context.fillStyle = edgeGradient;
    context.beginPath();
    context.moveTo(113.5, 477.8 - yOffset);
    context.bezierCurveTo(169.4, 539.1 - yOffset, 255.4, 571.5 - yOffset, 371.5, 575.2 - yOffset);
    context.lineTo(1136.2, 575.2 - yOffset);
    context.bezierCurveTo(1173.2, 575.2 - yOffset, 1202.3, 543.5 - yOffset, 1199.1, 506.6 - yOffset);
    context.lineTo(1198.1, 494.8 - yOffset);
    context.lineTo(225.2, 494.8 - yOffset);
    context.bezierCurveTo(177.8, 494.8 - yOffset, 140.6, 489.1 - yOffset, 113.5, 477.8 - yOffset);
    context.closePath();
    context.fill();
    context.globalAlpha = 1;

    context.strokeStyle = 'rgba(249, 248, 240, 0.32)';
    context.lineWidth = 8;
    context.lineCap = 'round';
    context.beginPath();
    context.moveTo(288, 391.2 - yOffset);
    context.bezierCurveTo(337.8, 374.6 - yOffset, 390.8, 366.2 - yOffset, 447, 366.2 - yOffset);
    context.lineTo(995, 366.2 - yOffset);
    context.stroke();

    context.fillStyle = 'rgba(29, 29, 27, 0.82)';
    context.beginPath();
    context.moveTo(1035, 575.2 - yOffset);
    context.lineTo(1134.4, 575.2 - yOffset);
    context.bezierCurveTo(1172.4, 575.2 - yOffset, 1202.2, 542.6 - yOffset, 1198.9, 504.8 - yOffset);
    context.lineTo(1190.5, 408.4 - yOffset);
    context.bezierCurveTo(1187.6, 377.1 - yOffset, 1161.4, 353.2 - yOffset, 1130, 353.2 - yOffset);
    context.lineTo(1035, 353.2 - yOffset);
    context.closePath();
    context.fill();

    context.save();
    context.translate(state.x, state.y);
    context.rotate((state.rotation * Math.PI) / 180);
    context.font = `${state.fontSize}px ${state.fontFamily}`;
    context.textAlign = 'center';
    context.textBaseline = 'middle';
    context.lineWidth = Math.max(2, state.fontSize * 0.035);
    context.strokeStyle = 'rgba(0, 0, 0, 0.42)';
    context.fillStyle = 'rgb(238, 238, 232)';
    context.strokeText(state.text, 0, 0);
    context.fillText(state.text, 0, 0);
    context.restore();

  }

  #buildPdf({ pageWidth, pageHeight, imageWidth, imageHeight, imageBytes, content }) {
    const encoder = new TextEncoder();
    const chunks = [];
    const offsets = [0];
    let length = 0;

    const pushString = (value) => {
      const bytes = encoder.encode(value);
      chunks.push(bytes);
      length += bytes.length;
    };
    const pushBytes = (bytes) => {
      chunks.push(bytes);
      length += bytes.length;
    };
    const startObject = (id) => {
      offsets[id] = length;
      pushString(`${id} 0 obj\n`);
    };

    pushString('%PDF-1.4\n%\xE2\xE3\xCF\xD3\n');

    startObject(1);
    pushString('<< /Type /Catalog /Pages 2 0 R >>\nendobj\n');

    startObject(2);
    pushString('<< /Type /Pages /Kids [3 0 R] /Count 1 >>\nendobj\n');

    startObject(3);
    pushString(
      `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${pageWidth} ${pageHeight}] /Resources << /XObject << /Im0 4 0 R >> >> /Contents 5 0 R >>\nendobj\n`
    );

    startObject(4);
    pushString(
      `<< /Type /XObject /Subtype /Image /Width ${imageWidth} /Height ${imageHeight} /ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /DCTDecode /Length ${imageBytes.length} >>\nstream\n`
    );
    pushBytes(imageBytes);
    pushString('\nendstream\nendobj\n');

    const contentBytes = encoder.encode(content);
    startObject(5);
    pushString(`<< /Length ${contentBytes.length} >>\nstream\n`);
    pushBytes(contentBytes);
    pushString('\nendstream\nendobj\n');

    const xrefOffset = length;
    pushString(`xref\n0 6\n0000000000 65535 f \n`);
    for (let id = 1; id <= 5; id += 1) {
      pushString(`${String(offsets[id]).padStart(10, '0')} 00000 n \n`);
    }
    pushString(`trailer\n<< /Size 6 /Root 1 0 R >>\nstartxref\n${xrefOffset}\n%%EOF`);

    const pdf = new Uint8Array(length);
    let position = 0;
    chunks.forEach((chunk) => {
      pdf.set(chunk, position);
      position += chunk.length;
    });

    return pdf;
  }

  #selectedText(select) {
    if (!select) return '';
    if (select.dataset.fontLabel) return select.dataset.fontLabel;

    return select.selectedOptions?.[0]?.textContent?.trim() || select.value;
  }

  #number(value, fallback) {
    const parsed = Number.parseFloat(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  #clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
  }

  #base64ToBytes(value) {
    const binary = atob(value);
    const bytes = new Uint8Array(binary.length);

    for (let index = 0; index < binary.length; index += 1) {
      bytes[index] = binary.charCodeAt(index);
    }

    return bytes;
  }
}

if (!customElements.get('engraving-customizer')) {
  customElements.define('engraving-customizer', EngravingCustomizer);
}
